import React, { useEffect, useState } from 'react'
import { CheckCircle, Circle } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import merchantApi from '../../services/merchantApi'
import './CreateVoucherPage.scss'
import Header from '../../components/Header/Header'
import { Grid } from '@mui/material'
import FormEditors from '../../components/FormEditors/FormEditors'

const moduleCode = 'voucher'

const ORDER_TYPES = ['Dine-in', 'Pickup', 'Kiosk']

function OptionCard({ active, onClick, title, description }) {
  return (
    <button type="button" className={`vdp-detail-form__option ${active ? 'vdp-detail-form__option--active' : ''}`} onClick={onClick}>
      <span className="vdp-detail-form__option-radio" />
      <span className="vb-option__body"><strong>{title}</strong><span>{description}</span></span>
    </button>
  )
}

function FormGroup({ label, hint, optional, error, children }) {
  return (
    <div className="vb-field">
      {label && <label className="vb-field__label">{label}{optional && <span className="vb-field__optional">optional</span>}</label>}
      {children}
      {error && <span className="vb-field__error">{error}</span>}
      {hint && !error && <p className="vb-field__hint">{hint}</p>}
    </div>
  )
}

function PrefixInput({ prefix, suffix, value, onChange, placeholder, disabled, error }) {
  return (
    <div className={`vb-prefix-input ${error ? 'vb-prefix-input--error' : ''} ${disabled ? 'vb-prefix-input--disabled' : ''}`}>
      {prefix && <span className="vb-prefix-input__tag">{prefix}</span>}
      <input type="text" inputMode="decimal" value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />
      {suffix && <span className="vb-prefix-input__suffix">{suffix}</span>}
    </div>
  )
}

const toApiDate = (date) => date ? date.toISOString().slice(0, 10) : null
const decimalOrNull = (value) => value === '' || value === null || value === undefined ? null : Number(value)
const integerOrNull = (value) => value === '' || value === null || value === undefined ? null : Number.parseInt(value, 10)

export default function CreateVoucherPage() {
  const navigate = useNavigate()
  const [discountType, setDiscountType] = useState('percentage')
  const [scope, setScope] = useState('merchant')
  const [selectedOutlets, setSelectedOutlets] = useState([])
  const [orderTypes, setOrderTypes] = useState([])
  const [validFromEnabled, setValidFromEnabled] = useState(false)
  const [validUntilEnabled, setValidUntilEnabled] = useState(false)
  const [validFrom, setValidFrom] = useState(null)
  const [validUntil, setValidUntil] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [apiError, setApiError] = useState('')
  const [outletList, setOutletList] = useState(null)
  const [orderTypeList, setOrderTypeList] = useState(null)

  useEffect(() => {
    loadUserInfo()
    loadGeneralInfo()
    loadOutletList()
  }, [])

  const loadUserInfo = async () => {
    try {
      const response = await merchantApi.auth.me()
      if (!response?.data?.permissions?.includes(`create-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }
    } catch (error) {
      console.error('Error loading user info:', error)
      setApiError(error || 'Unable to load user info. Please refresh the page or try again later.')
    }
  }

  const loadGeneralInfo = async () => {
    try {
      const response = await merchantApi.general.systemInfo()
      setOrderTypeList(response?.data?.order_types || null)
    } catch (error) {
      console.error('Error loading outlet list:', error)
      setApiError(error || 'Unable to load order type list. Please refresh the page or try again later.')
    }
  }
  const loadOutletList = async () => {
    try {
      const response = await merchantApi.outlets.list({ page: 0 })
      setOutletList(response?.data || null)
    } catch (error) {
      console.error('Error loading outlet list:', error)
      setApiError(error || 'Unable to load outlet list. Please refresh the page or try again later.')
    }
  }

  const [formData, setFormData] = useState({
    voucherName: '', discountValue: '', maxDiscountCap: '',
    pointsRequired: '', totalClaimLimit: '', usageLimitPerCustomer: '',
    minimumOrderSpend: '', terms: '',
  })
  const [errors, setErrors] = useState({})

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }))
    setErrors((p) => ({ ...p, [field]: '' }))
    setApiError('')
  }

  const toggleOutlet = (outlet) => setSelectedOutlets((p) => p.some((x) => x.id === outlet.id) ? p.filter((x) => x.id !== outlet.id) : [...p, outlet])
  const toggleOrderType = (t) => setOrderTypes((p) => p.includes(t) ? p.filter((x) => x !== t)?.value || [] : [...p, t])
  console.log('selectedOrderType', orderTypes)
  const validate = () => {
    const e = {}
    if (!formData.voucherName.trim()) e.voucherName = 'Voucher name is required'
    if (!formData.discountValue.trim()) e.discountValue = 'Discount amount is required'
    else if (!/^\d*\.?\d+$/.test(formData.discountValue)) e.discountValue = 'Must be numeric'
    if (formData.maxDiscountCap && !/^\d*\.?\d+$/.test(formData.maxDiscountCap)) e.maxDiscountCap = 'Must be numeric'
    if (!formData.pointsRequired.trim()) e.pointsRequired = 'Points required'
    else if (!/^\d+$/.test(formData.pointsRequired)) e.pointsRequired = 'Must be an integer'
    if (formData.minimumOrderSpend && !/^\d*\.?\d+$/.test(formData.minimumOrderSpend)) e.minimumOrderSpend = 'Must be numeric'
    if (validFromEnabled && !validFrom) e.validFrom = 'Select a start date'
    if (validUntilEnabled && !validUntil) e.validUntil = 'Select an end date'
    if (scope === 'outlet' && !selectedOutlets.length) e.selectedOutlets = 'Select at least one outlet'
    if (validFrom && validUntil && validUntil < validFrom) e.validUntil = 'Must be after start date'
    setErrors(e)
    return !Object.keys(e).length
  }

  const buildPayload = (status = 1) => ({
    name: formData.voucherName.trim(), //
    terms_and_conditions: formData.terms || null,
    points_required: integerOrNull(formData.pointsRequired), //
    // points_needed: integerOrNull(formData.pointsRequired),
    discount_type: discountType === 'percentage' ? 'percentage_off' : 'fixed_amount_off', //
    fixed_amount: decimalOrNull(formData.discountValue),
    discount_amount: discountType === 'fixed_amount_off' ? decimalOrNull(formData.discountValue) : null,
    discount_percentage: discountType === 'percentage' ? decimalOrNull(formData.discountValue) : null,//
    max_discount_cap: decimalOrNull(formData.maxDiscountCap), //
    // has_min_spend: Boolean(formData.minimumOrderSpend),
    minimum_order_spend: decimalOrNull(formData.minimumOrderSpend),
    usage_limit_per_customer: integerOrNull(formData.usageLimitPerCustomer),
    quantity: integerOrNull(formData.totalClaimLimit),
    outlet_ids: scope === 'outlet' ? selectedOutlets.map((outlet) => outlet.id) : [],
    valid_order_types: orderTypes, //
    valid_from: validFromEnabled ? toApiDate(validFrom) : null,
    valid_until: validUntilEnabled ? toApiDate(validUntil) : null,
    action: status == 0 ? 'save_draft' : 'publish', //
    scope: scope === 'outlet' ? 'outlet_specific' : 'merchant_wide', //
  })

  const submit = async (status) => {
    // if (!validate()) return
    setSubmitting(true)
    setApiError('')
    try {
      await merchantApi.vouchers.reward.create(buildPayload(status))
      navigate('/vouchers')
    } catch (err) {
      setApiError(err.message || 'Unable to create reward voucher')
      if (err?.data?.errors) setErrors(err.data.errors)
    } finally {
      setSubmitting(false)
    }
  }
console.log("Error", errors)
  return (
    <div className="vb-page">
      <div className="vb-page__header">
        <Header
          showBack={true}
          backLabel="Back"
          onBack={() => navigate('/vouchers')}
          items={[
            { label: 'Vouchers', to: '/vouchers' },
            { label: <>Create reward voucher <span className="vb-page__badge vb-page__badge--teal">Points-based</span></>, current: true },
          ]}
        />
        <div className="vb-page__actions">
          <button className="vb-btn vb-btn--ghost" type="button" onClick={() => navigate('/vouchers')}>Discard</button>
          <button className="vb-btn vb-btn--outline" type="button" disabled={submitting} onClick={() => submit(0)}>Save draft</button>
          <button className="vb-btn vb-btn--primary" type="button" disabled={submitting} onClick={() => submit(1)}>{submitting ? 'Publishing...' : 'Publish'}</button>
        </div>
      </div>

      {apiError && <div className="api-error">{apiError}</div>}

      <div className="vb-card">
        <div className="vb-card__header">Voucher type &amp; discount</div>

        <div className="vb-card__body">
          <Grid container spacing={2}>
            <Grid item sm={12}>
              <FormGroup label="Voucher name" hint="Shown as the voucher title in the customer loyalty wallet. Keep it under 40 characters." error={errors?.voucherName || errors?.name}>
                <input className={`vb-input ${errors?.voucherName || errors?.name ? 'vb-input--error' : ''}`} type="text" placeholder="e.g. 10% off your order" value={formData.voucherName} onChange={(e) => handleChange('voucherName', e.target.value)} />
              </FormGroup>
            </Grid>
            <Grid item sm={12}>
              <FormGroup label="Discount type">
                <div className="vb-option-grid">
                  <OptionCard active={discountType === 'percentage'} onClick={() => setDiscountType('percentage')} title="Percentage off" description="A % discount on the order subtotal. E.g. 10% off." />
                  <OptionCard active={discountType === 'fixed_amount_off'} onClick={() => setDiscountType('fixed_amount_off')} title="Fixed amount off" description="A fixed RM value deducted from the order total." />
                </div>
              </FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label={discountType === 'percentage' ? 'Discount percentage' : 'Discount amount'} hint="Applied to order subtotal before tax" error={errors?.discountValue || errors?.discount_value}>
                <PrefixInput prefix={discountType === 'fixed_amount_off' ? 'RM' : undefined} suffix={discountType === 'percentage' ? '%' : undefined} value={formData.discountValue} placeholder="0" error={errors?.discountValue || errors?.discount_value} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('discountValue', e.target.value) }} />
              </FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label="Max discount cap" optional hint="Limits the max RM saved per redemption" error={errors?.maxDiscountCap || errors?.max_discount_cap}>
                <PrefixInput prefix="RM" placeholder="No cap" value={formData.maxDiscountCap} error={errors?.maxDiscountCap || errors?.max_discount_cap} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('maxDiscountCap', e.target.value) }} />
              </FormGroup>
            </Grid>
            <Grid item sm={12}>
              <FormGroup label="Points required" hint="Define the number of loyalty points needed to redeem the voucher" error={errors?.pointsRequired || errors?.points_required}>
                <input className={`vb-input ${errors?.pointsRequired || errors?.points_required ? 'vb-input--error' : ''}`} type="text" inputMode="numeric" value={formData.pointsRequired} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange('pointsRequired', e.target.value) }} />
              </FormGroup>
            </Grid>
            <Grid item sm={12}>
              <FormGroup label="Terms and conditions (if applicable)">
                <FormEditors
                  value={formData.terms}
                  setValue={(value) => handleChange('terms', value)}
                  placeholder="Enter terms and conditions here..."
                  maxWords={200}
                />
              </FormGroup>
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="vb-card">
        <div className="vb-card__header">Scope &amp; usage rules</div>
        <div className="vb-card__body">
          <Grid container spacing={2}>
            <Grid item sm={12}>
              <FormGroup label="Scope">
                <div className="vb-option-grid">
                  <OptionCard active={scope === 'merchant'} onClick={() => setScope('merchant')} title="Merchant-wide" description="Available at all outlets. Customers at any branch can redeem this reward." />
                  <OptionCard active={scope === 'outlet'} onClick={() => setScope('outlet')} title="Outlet specific" description="Available only at selected outlets." />
                </div>
                {scope === 'outlet' && <div className="vb-outlet-picker"><p className="vb-outlet-picker__label">Select outlets</p>
                  <div className="vb-chips">
                    {
                      outletList?.length > 0 ? outletList.map((o) =>
                        <button
                          key={o.id}
                          type="button"
                          className={`vb-chip ${selectedOutlets.some((x) => x.id === o.id) ? 'vb-chip--active' : ''}`}
                          onClick={() => toggleOutlet(o)}>
                          {o.name}
                        </button>)
                        : <p className="vb-outlet-picker__empty">No outlets found</p>
                    }
                  </div>
                  {errors?.selectedOutlets && <span className="vb-field__error">{errors?.selectedOutlets}</span>}
                </div>
                }
              </FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label="Total claim limit" hint="Leave blank for no limit"><input className="vb-input" type="text" inputMode="numeric" placeholder="Unlimited" value={formData.totalClaimLimit} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange('totalClaimLimit', e.target.value) }} /></FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label="Usage limit per customer" hint="Leave blank for no limit"><input className="vb-input" type="text" inputMode="numeric" placeholder="Unlimited" value={formData.usageLimitPerCustomer} onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleChange('usageLimitPerCustomer', e.target.value) }} /></FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label="Minimum order spend" hint="Optional minimum subtotal" error={errors?.minimumOrderSpend || errors?.min_spend}><PrefixInput prefix="RM" placeholder="None" value={formData.minimumOrderSpend} error={errors?.minimumOrderSpend || errors?.min_spend} onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) handleChange('minimumOrderSpend', e.target.value) }} /></FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup label="Valid for order types" error={errors?.valid_order_types?.[0]}><div className="vb-chips">
                {
                  orderTypeList?.length > 0 ? orderTypeList.map((item) =>
                    <button
                      key={item?.value}
                      type="button"
                      className={`vb-chip ${orderTypes.includes(item?.value) ? 'vb-chip--active' : ''}`}
                      onClick={() => toggleOrderType(item?.value)}>{item?.label}</button>)
                    : <p className="vb-outlet-picker__empty">No order types found</p>
                }
              </div>
              </FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup hint="No start date restriction" error={errors?.validFrom || errors?.start_at}><div className="vb-field__label-row"><label className="vb-field__label">Valid from</label><label className="vb-checkbox"><input type="checkbox" checked={validFromEnabled} onChange={(e) => { setValidFromEnabled(e.target.checked); if (!e.target.checked) setValidFrom(null) }} /><span>Enable</span></label></div><DatePicker selected={validFrom} onChange={setValidFrom} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" className={`vb-input ${errors?.validFrom || errors?.start_at ? 'vb-input--error' : ''}`} disabled={!validFromEnabled} onKeyDown={(e) => e.preventDefault()} /></FormGroup>
            </Grid>
            <Grid item sm={6} xs={12}>
              <FormGroup hint="Leave blank for no expiry" error={errors?.validUntil || errors?.end_at}><div className="vb-field__label-row"><label className="vb-field__label">Valid until</label><label className="vb-checkbox"><input type="checkbox" checked={validUntilEnabled} onChange={(e) => { setValidUntilEnabled(e.target.checked); if (!e.target.checked) setValidUntil(null) }} /><span>Enable</span></label></div><DatePicker selected={validUntil} onChange={setValidUntil} dateFormat="dd/MM/yyyy" placeholderText="dd/mm/yyyy" className={`vb-input ${errors?.validUntil || errors?.end_at ? 'vb-input--error' : ''}`} disabled={!validUntilEnabled} onKeyDown={(e) => e.preventDefault()} /></FormGroup>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  )
}
